import { useLocation } from 'react-router-dom';

/**
 * One ancestor dialog the user passed through: a readable label ("Planum 1",
 * "Befund 5") plus its URL. Carried in router state, so it must stay
 * JSON-serialisable.
 */
export interface DialogCrumb {
    label: string;
    /**
     * Identifies the entry; it is never pushed again, because the back step
     * walks HISTORY (see `useDialogBackCrumb`).
     */
    to: string;
}

/** Router `location.state` shape the dialogs read/write for their navigation. */
export interface DialogBackState {
    backTrail?: DialogCrumb[];
    /**
     * How many "next/previous record" steps this entry sits above the dialog
     * the user actually opened (see {@link useSiblingNavState}). Absent (0) for
     * a dialog that was opened rather than paged to.
     */
    paged?: number;
    /**
     * The paged-from dialog was a DEEP LINK, so there is no in-app entry beneath
     * the chain to walk back to. Carried along the chain because only its first
     * entry can still tell (`location.key === 'default'`).
     */
    pagedFromDeepLink?: boolean;
    /**
     * How many history entries the dialogs this one was reached FROM stand on
     * (see {@link useForwardBackState}) — what closing has to walk back over,
     * besides its own, to land on the list the chain began at.
     */
    chainSteps?: number;
    /** The chain's first dialog was a deep link: no list underneath it. */
    chainFromDeepLink?: boolean;
}

/**
 * The chain of dialogs the user navigated through to reach the current one,
 * oldest first — empty at the start of a chain. A dialog linking onward passes
 * {@link useForwardBackState} as the navigation's state, appending its own
 * crumb.
 */
export function useBackTrail(): DialogCrumb[] {
    const { state } = useLocation();
    const trail = (state as DialogBackState | null)?.backTrail;
    return Array.isArray(trail) ? trail : [];
}

/**
 * The router state a dialog attaches when navigating ONWARD to another record:
 * the incoming trail plus its own crumb, so the next dialog can show a "back to
 * {selfLabel}" button — and the entries the chain stands on so far (this
 * dialog's own, plus the records it paged past), so closing the next one can
 * walk out to the list in one go.
 */
export function useForwardBackState(selfLabel: string): DialogBackState {
    const trail = useBackTrail();
    const { pathname, search, state, key } = useLocation();
    const current = (state as DialogBackState | null) ?? {};
    return {
        backTrail: [...trail, { label: selfLabel, to: pathname + search }],
        chainSteps: (current.chainSteps ?? 0) + (current.paged ?? 0) + 1,
        chainFromDeepLink: Boolean(
            current.chainFromDeepLink ||
            current.pagedFromDeepLink ||
            key === 'default'
        )
    };
}

/**
 * The labelled back-crumb for the dialog app bar, or `undefined` at the root of
 * a chain — it only names where "back" leads; the step itself is the host's
 * guarded BACK (`closeGuard.requestBack`), so it prompts on unsaved edits like
 * any other exit, and leaves only this dialog where Schließen leaves the chain.
 *
 * Stepping back rather than navigating to `previous.to` is the point: a crumb exists
 * only because this dialog was PUSHED from that one, so the entry below us in
 * history already IS it (see `useDialogClose`). Pushing `previous.to` would
 * stack a second copy on top, and the next close — which walks history, not the
 * trail — would land back INSIDE that record instead of out of the chain.
 */
export function useDialogBackCrumb(
    requestClose: () => void
): { label: string; onOpen: () => void } | undefined {
    const trail = useBackTrail();
    const previous = trail[trail.length - 1];
    if (!previous) {
        return undefined;
    }
    return { label: previous.label, onOpen: requestClose };
}

/**
 * The router state a dialog attaches when paging to its NEIGHBOUR — the record
 * before or after it in the list (see `useRecordSiblings`). Paging is not a step
 * deeper into a chain; it is the SAME dialog showing the next record, so it
 * differs from {@link useForwardBackState} twice over:
 *
 * - The trail passes through unchanged. Appending would offer a "back to the
 *   record before this one" nobody asked for.
 * - The steps are COUNTED. Each hop leaves the record it came from standing in
 *   history, so without the count `useDialogClose` would step back one record
 *   instead of out to the list — twenty presses after twenty days of paging.
 */
export function useSiblingNavState(): DialogBackState {
    const { state, key } = useLocation();
    const current = (state as DialogBackState | null) ?? {};
    return {
        ...(current.backTrail ? { backTrail: current.backTrail } : {}),
        ...(current.chainSteps
            ? {
                  chainSteps: current.chainSteps,
                  chainFromDeepLink: current.chainFromDeepLink
              }
            : {}),
        paged: (current.paged ?? 0) + 1,
        pagedFromDeepLink: current.pagedFromDeepLink ?? key === 'default'
    };
}

/**
 * How many extra history entries this dialog's paging has stacked up, and
 * whether the chain began on a deep link — read back by `useDialogClose`.
 */
export function usePagedSteps(): { steps: number; fromDeepLink: boolean } {
    const { state } = useLocation();
    const current = (state as DialogBackState | null) ?? {};
    return {
        steps: current.paged ?? 0,
        fromDeepLink: current.pagedFromDeepLink ?? false
    };
}

/**
 * The entries under this dialog that belong to the dialogs it was reached from,
 * and whether that chain began on a deep link — read back by `useDialogClose`.
 */
export function useChainSteps(): { steps: number; fromDeepLink: boolean } {
    const { state } = useLocation();
    const current = (state as DialogBackState | null) ?? {};
    return {
        steps: current.chainSteps ?? 0,
        fromDeepLink: current.chainFromDeepLink ?? false
    };
}

/**
 * How far a dialog is left: `chain` (Schließen, Abbrechen, Esc) goes back to the
 * list the user started on, past every dialog they followed a link through;
 * `step` (the back crumb, the browser's back button, a save) returns only to
 * the dialog before it.
 */
export type CloseScope = 'chain' | 'step';

/** What leaving a dialog has to do to the history stack. */
export type DialogCloseStep =
    { kind: 'back'; steps: number } | { kind: 'parent' };

/**
 * How far back a closing dialog has to walk — the whole of `useDialogClose`'s
 * decision as a function of history, so the counting can be checked without a
 * browser.
 *
 * The stack under an open dialog, from the bottom: the page it was opened from,
 * one entry per record (the one opened, plus one per "next/previous" step paged
 * since), and the close guard's sentinel on top. Closing means shedding
 * everything above that first entry.
 *
 * Leaving the whole chain sheds the dialogs it was reached from, too.
 *
 * `onSentinel` is false when the browser's own back button already popped it —
 * that press is what triggered the close, and the entry is gone.
 */
export function dialogCloseStep({
    onSentinel,
    isDeepLink,
    paged,
    chain,
    scope
}: {
    /** The guard's sentinel is still the current entry. */
    onSentinel: boolean;
    /** The dialog IS the first in-app entry (`location.key === 'default'`). */
    isDeepLink: boolean;
    paged: { steps: number; fromDeepLink: boolean };
    chain: { steps: number; fromDeepLink: boolean };
    scope: CloseScope;
}): DialogCloseStep {
    const toList = scope === 'chain';
    // Nothing in-app underneath: the dialog was deep-linked, or every entry
    // below it is a record the user paged past from one. Walking back would
    // leave the site, so the parent page takes the current entry's place.
    if (isDeepLink || paged.fromDeepLink || (toList && chain.fromDeepLink)) {
        return { kind: 'parent' };
    }
    return {
        kind: 'back',
        steps: (onSentinel ? 2 : 1) + paged.steps + (toList ? chain.steps : 0)
    };
}
